import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { parseSearch } from 'app/utils/lib/string';

import { NavigationDispatchContext, NavigationStateContext, QueryStringKey } from './contexts';
import { QueryParamMap } from './types';

export const useNavigationDispatch = () =>
  useContext(NavigationDispatchContext);

export const useNavigationState = () =>
  useContext(NavigationStateContext);

export const useQueryString = (key: QueryStringKey | undefined) => {
  const { queryStrings } = useNavigationState();

  return key ? queryStrings[key] : null;
};

const useSubscribeToSearch = (queryStringKey: QueryStringKey) => {
  const { search } = useLocation();

  const setQueryString = useQueryStringSetter();

  useEffect(() => {
    setQueryString({
      queryString: search,
      queryStringKey,
    });
  },
  [queryStringKey, search, setQueryString]);
};

export const useQueryParams = <T extends QueryStringKey>(key: T): NonNullable<QueryParamMap[T]> => {
  const queryString = useQueryString(key);
  useSubscribeToSearch(key);

  return useMemo(() => parseSearch(queryString ?? '') as NonNullable<QueryParamMap[T]>,
    [queryString]);
};

export const useQueryStringSetter = () => {
  const navigationDispatch = useNavigationDispatch();

  return useCallback(({
    queryString = window.location.search,
    queryStringKey,
  }: {
    queryString?: string;
    queryStringKey: QueryStringKey;
  }) => {
    navigationDispatch({
      payload: {
        queryStrings: {
          [queryStringKey]: queryString,
        },
      },
      type: 'setQueryStrings',
    });
  },
  [navigationDispatch]);
};
